<template>
  <v-card v-resize-observer="onResize" class="company-page h-100">
    <v-card-title class="pa-0 pl-2">
      <div class="ma-2 d-flex">
        <HomeButton />
        <span class="mx-2">|</span>
        <span
          class="subtitle-2 black--text text--lighten-4 d-flex align-center"
        >
          <LogoDisplayer
            :domain="companyDetails.exid"
            :size="16"
            class="mr-1"
          />
          <span class="mx-1">{{ companyDetails.name || companyId }}</span>
          <a :href="`//${companyDetails.exid || companyId}`" target="_blank">
            {{ `(${companyDetails.exid || companyId})` }}
          </a>
        </span>
        <v-btn class="close-icon" color="primary" icon dark @click="goBack">
          <v-icon>fa-times</v-icon>
        </v-btn>
      </div>
    </v-card-title>
    <ETabs
      v-model="currentTab"
      :vertical="$vuetify.breakpoint.mdAndUp"
      class="custom-tabs"
      active-class="blue lighten-5"
    >
      <div
        v-for="tab in tabs"
        :key="tab.value"
        :name="tab.name"
        class="px-2 w-100 company-page__content"
      >
        <div v-if="showTab(tab, COMPANY_TABS.TABLES)">
          <ERow no-gutters>
            <ECol :cols="$vuetify.breakpoint.lgAndUp ? 5 : 12">
              <CompaniesDetails :company-details="companyDetails" />
            </ECol>
          </ERow>
          <CompaniesProjectsTable :company-id="companyDetails.id" />
          <CompaniesCamerasTable :company-id="companyDetails.id" />
          <CompaniesUsersTable :company-id="companyDetails.id" />
          <ProjectHdds :company-id="companyDetails.id" />
        </div>
        <div v-if="showTab(tab, COMPANY_TABS.EVENTS)">
          <PosthogEvents
            :company-exid="companyDetails?.exid"
            :company-id="companyDetails?.id"
            :is-displayed-internal-data="true"
          />
          <PosthogEventsStats
            :company-exid="companyDetails?.exid"
            :company-id="companyDetails?.id"
          />
        </div>
        <ZohoDeskTicketsTable
          v-if="showTab(tab, COMPANY_TABS.TICKETS)"
          :company-name="companyDetails.name"
          :height="height"
        />
      </div>
    </ETabs>
  </v-card>
</template>

<script>
import LogoDisplayer from "@evercam/shared/components/LogoDisplayer"
import PosthogEvents from "@evercam/shared/components/posthog/PosthogEvents"
import PosthogEventsStats from "@evercam/shared/components/posthog/PosthogEventsStats"
import CompaniesDetails from "@/components/companies/CompaniesDetails"
import CompaniesProjectsTable from "@/components/companies/CompaniesProjectsTable"
import CompaniesCamerasTable from "@/components/companies/CompaniesCamerasTable"
import CompaniesUsersTable from "@/components/companies/CompaniesUsersTable"
import ProjectHdds from "@/components/hdds/ProjectHdds"
import ZohoDeskTicketsTable from "@/components/zohoDesk/ZohoDeskTicketsTable.vue"
import { EvercamApi } from "@evercam/shared/api/evercamApi"
import HomeButton from "@/components/HomeButton"
import { PERMISSIONS } from "@/constants/permissions"
import { COMPANY_TABS } from "@/components/constants.js"
import { useNuxtApp } from "#app"

export default {
  meta: {
    requiredPermissions: [PERMISSIONS.COMPANIES.VIEW],
  },
  name: "Company",
  components: {
    LogoDisplayer,
    HomeButton,
    CompaniesDetails,
    CompaniesProjectsTable,
    CompaniesCamerasTable,
    CompaniesUsersTable,
    PosthogEvents,
    PosthogEventsStats,
    ProjectHdds,
    ZohoDeskTicketsTable,
  },
  async asyncData({ params, error }) {
    return {
      error,
      companyId: params.id,
    }
  },
  data() {
    return {
      currentTab: COMPANY_TABS.TABLES,
      companyDetails: {},
      height: undefined,
      COMPANY_TABS,
    }
  },
  computed: {
    tabs() {
      return [
        {
          name: "TABLES",
          value: COMPANY_TABS.TABLES,
        },
        {
          name: "EVENTS",
          value: COMPANY_TABS.EVENTS,
        },
        {
          name: "TICKETS",
          value: COMPANY_TABS.TICKETS,
        },
      ]
    },
  },
  async mounted() {
    let response
    try {
      response = await EvercamApi.companies.show(
        this.companyId,
        this.$route.query
      )
    } catch (e) {
      if ("with_zoho" in this.$route.query && e.response.status === 404) {
        useNuxtApp().nuxt2Context.error({
          statusCode: 404,
          message: "This account is not found in Evercam",
          homePageLink: false,
        })

        return
      } else if (e?.response?.status === 404) {
        useNuxtApp().nuxt2Context.error({
          statusCode: 404,
          message: "Company not found",
          homePageLink: true,
        })

        return
      } else {
        this.$notifications.error({
          text: "Load product analytics company details failed!",
          error: e,
        })
      }
    }

    const companyDetails = response

    this.companyDetails = companyDetails || {}
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    showTab(currentTab, tabId) {
      return currentTab.value === tabId
    },
    onResize({ contentRect }) {
      this.height = contentRect.height - 88
    },
  },
}
</script>

<style lang="scss">
@import "~@evercam/shared/styles/mixins";
@import "~vuetify/src/styles/styles.sass";
.company-page {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .e-tabs .tab-content .tab-content {
    padding-bottom: 0px !important;
    overflow-x: hidden;
    width: 100% !important;
  }

  .e-tabs .tab-content,
  .e-tabs .tab-content > div {
    height: calc(100vh - 48px);
    width: calc(100% - 134px);
    @include custom-scrollbar(rgb(186, 186, 186), rgb(231, 231, 231));
  }

  .e-tabs__header {
    width: 134px;
    min-width: 134px;
    box-shadow: 5px 0px 11px -12px rgba(0, 0, 0, 0.7098);
    z-index: 1;
    border-right-style: none;
  }

  .e-tabs__item {
    line-height: 1.375rem;
    font-size: 0.8rem !important;
    letter-spacing: 0.0071em !important;
    padding: 8px !important;
    border-color: #2196f3 !important;
  }
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    max-height: calc(100vh - 93px);
  }
}
</style>
